'use client';

import { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Sparkles, X, Send, Loader2, Zap, ChevronRight } from 'lucide-react';
import { getNextProxyBase } from '@/lib/nextProxy';
import { withThemeQuery } from '@/lib/themeSync';
import type { Category } from '@/lib/api';

const SUGGESTED_PROMPTS = [
  'How do I create an account?',
  'Find used cars under 50 Lakhs',
  'How do I post an ad?',
  'Take me to the Help page',
  'Show me Electronics',
];

interface SmartAssistantProps {
  categories: Category[];
  locations?: string[];
}

export function SmartAssistant({ categories = [], locations = [] }: SmartAssistantProps) {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<{ role: string; text: string; actionPerformed?: string }[]>([
    {
      role: 'model',
      text: "Hi! I'm your guidance & FAQ assistant. I can help you find deals, filter by price/location/brand/condition, navigate categories, and answer questions about using BuyMe.lk. Ask me anything about the site or how to get started.",
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isOpen]);

  const resolveAssistantPath = (rawPath: string) => {
    const path = rawPath.trim();

    // External URL returned by assistant
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return { type: 'external' as const, url: path };
    }

    // Safety: only handle app-relative paths
    if (!path.startsWith('/')) return null;

    // Map Laravel/legacy routes to Next.js routes
    if (path === '/browse' || path === '/browse/') {
      return { type: 'internal' as const, url: '/search' };
    }
    if (path.startsWith('/category/')) {
      const categoryIdOrSlug = path.replace('/category/', '').split('/')[0];
      if (categoryIdOrSlug) {
        return { type: 'internal' as const, url: `/search/${encodeURIComponent(categoryIdOrSlug)}` };
      }
    }

    // Keep known Next.js routes internal
    const nextRoutes = ['/search', '/categories', '/dashboard', '/'];
    if (nextRoutes.some((route) => path === route || path.startsWith(`${route}?`))) {
      return { type: 'internal' as const, url: path };
    }

    // Laravel-only routes: same-origin path (next.config rewrites to Laravel)
    return { type: 'external' as const, url: path };
  };

  const handleSend = async (textOverride?: string) => {
    const userText = (textOverride ?? input).trim();
    if (!userText) return;

    setMessages((prev) => [...prev, { role: 'user', text: userText }]);
    setInput('');
    setLoading(true);

    try {
      // Call same-origin Next.js API to avoid mixed-content/CORS issues on HTTPS.
      const nx = getNextProxyBase();
      const res = await fetch(`${nx}/assistant/ask`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userText,
          context: 'User is browsing the site',
          categories: (categories || []).map((c) => ({ id: String(c?.id ?? ''), name: String(c?.name ?? '') })),
          locations: Array.isArray(locations) ? locations.map((l) => String(l ?? '')) : [],
        }),
      });

      let data: any = {};
      const contentType = res.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        data = await res.json();
      } else {
        const text = await res.text();
        throw new Error(`Assistant API returned non-JSON response (${res.status}): ${text.slice(0, 200)}`);
      }

      if (!res.ok) {
        throw new Error(data?.message || `Assistant API request failed (${res.status})`);
      }

      if (data.rateLimited) {
        setMessages((prev) => [
          ...prev,
          { role: 'model', text: data.message || "You've reached the limit of 15 questions per 5 minutes. Please wait a few minutes before asking again." },
        ]);
        setLoading(false);
        return;
      }

      let actionFeedback = '';

      if (data.intent === 'FILTER' || data.intent === 'NAVIGATE') {
        if (data.categoryNav?.slug) {
          router.push(`/search/${encodeURIComponent(String(data.categoryNav.slug))}`);
          actionFeedback = `Navigated to ${data.categoryNav.name ?? 'category'}`;
        } else if (data.categoryNav?.id != null) {
          const found = categories.find((c) => String(c.id) === String(data.categoryNav.id));
          if (found?.slug) {
            router.push(`/search/${encodeURIComponent(found.slug)}`);
          } else {
            router.replace(`/search?category=${data.categoryNav.id}`);
          }
          actionFeedback = `Navigated to ${data.categoryNav.name ?? 'category'}`;
        }
      } else if (data.intent === 'NAVIGATE_PAGE' && data.pageNav?.path) {
        const target = resolveAssistantPath(String(data.pageNav.path));
        if (target?.type === 'internal') {
          router.push(target.url);
        } else if (target?.type === 'external') {
          window.location.href = withThemeQuery(target.url);
        }
        actionFeedback = `Opening ${data.pageNav.name ?? 'page'}`;
      }

      setMessages((prev) => [
        ...prev,
        { role: 'model', text: data.message ?? 'Done.', actionPerformed: actionFeedback || undefined },
      ]);
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : "Sorry, I couldn't process that. Please try again or visit the Help page.";
      setMessages((prev) => [
        ...prev,
        { role: 'model', text: message },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-20 md:bottom-6 right-4 md:right-6 z-50 p-3 md:p-4 rounded-full shadow-xl transition-all duration-300 flex items-center gap-2 border-2 border-white ${
          isOpen ? 'bg-brand-dark' : 'bg-gradient-to-r from-brand-orange to-orange-500 hover:scale-110'
        }`}
        aria-label={isOpen ? 'Close AI assistant' : 'Open AI assistant'}
      >
        {isOpen ? (
          <X className="text-white w-6 h-6" aria-hidden />
        ) : (
          <Sparkles className="text-white w-6 h-6 animate-pulse" aria-hidden />
        )}
        {!isOpen && (
          <div className="hidden md:flex flex-col items-start -space-y-1 pr-2">
            <span className="text-white font-black text-sm">Ai Agent</span>
            <span className="text-[10px] text-white/80 font-medium">Find deals · Navigate · Help</span>
          </div>
        )}
      </button>

      {isOpen && (
        <div className="fixed bottom-36 md:bottom-24 right-4 md:right-6 z-40 w-[calc(100%-2rem)] md:w-[400px] bg-white rounded-3xl shadow-2xl border border-neutral-200 flex flex-col overflow-hidden h-[65vh] md:h-[600px]">
          <div className="bg-brand-dark p-4 flex items-center justify-between relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-brand-orange/20 to-transparent opacity-50" />
            <div className="flex items-center gap-3 relative z-10">
              <div className="bg-white/10 p-2 rounded-full backdrop-blur-md">
                <Sparkles size={20} className="text-brand-orange fill-brand-orange" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg leading-none">Ai Agent</h3>
                <span className="text-xs text-gray-400">Deals, navigation & help</span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="text-white/50 hover:text-white z-10"
              aria-label="Close AI assistant"
            >
              <X size={20} aria-hidden />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 bg-neutral-50 space-y-4">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                <div
                  className={`max-w-[85%] p-3.5 rounded-2xl text-sm leading-relaxed shadow-sm ${
                    msg.role === 'user'
                      ? 'bg-brand-orange text-white rounded-tr-none'
                      : 'bg-white border border-neutral-100 text-gray-700 rounded-tl-none'
                  }`}
                >
                  {msg.text}
                </div>
                {msg.actionPerformed && (
                  <div className="mt-1.5 flex items-center gap-1.5 text-[10px] font-bold text-brand-green bg-green-50 px-2 py-1 rounded-full border border-green-100">
                    <Zap size={10} className="fill-current" /> {msg.actionPerformed}
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-neutral-100 p-4 rounded-2xl rounded-tl-none shadow-sm flex items-center gap-3">
                  <Loader2 size={18} className="animate-spin text-brand-orange" />
                  <span className="text-xs text-gray-500 font-medium animate-pulse">Processing...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {!loading && messages.length < 4 && (
            <div className="px-4 pb-2 flex gap-2 overflow-x-auto no-scrollbar">
              {SUGGESTED_PROMPTS.map((prompt, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => handleSend(prompt)}
                  className="whitespace-nowrap bg-white border border-neutral-200 hover:border-brand-orange/50 hover:text-brand-orange text-xs font-medium text-neutral-600 px-3 py-1.5 rounded-full transition-colors shadow-sm flex items-center gap-1"
                >
                  {prompt} <ChevronRight size={10} />
                </button>
              ))}
            </div>
          )}

          <div className="p-3 bg-white border-t border-neutral-100">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSend();
              }}
              className="flex items-center gap-2"
            >
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about the site, find deals, or get help..."
                className="flex-1 bg-neutral-100 border-transparent focus:bg-white focus:border-brand-orange focus:ring-4 focus:ring-brand-orange/10 rounded-xl px-4 py-3 text-sm transition-all outline-none"
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="p-3 bg-brand-dark text-white rounded-xl hover:bg-neutral-800 disabled:opacity-50 disabled:hover:bg-brand-dark transition-all shadow-lg"
              >
                <Send size={18} />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
